/**
 * PATH: packages/email-hub/src/feature/reader-header/model/types.ts
 * FILE_ID: EMAILHUB-READERHEADER-MODEL-TYPES-001
 * PURPOSE: Define the shared whole-reader header contracts (Sprint 0, change #269).
 * ROLE IN ARCHITECTURE: Domain contracts consumed by preview and full-view adapters.
 * MAIN EXPORTS: ReaderActionDescriptor, ReaderDecision, ReaderNotice, ReaderHeaderViewModel.
 * NON-RESPONSIBILITIES: This file does not render UI or perform mutations.
 * NOTES FOR FUTURE AI: Extend unions and the registry together; never create host-only action semantics.
 */
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

export type ReaderHeaderSurface = 'preview' | 'full'

export type ReaderHeaderZone =
  | 'orientation'
  | 'communication'
  | 'decision'
  | 'context'
  | 'workflow'
  | 'notice'
  | 'overflow'

export type ReaderInputMethod =
  | 'pointer'
  | 'context-menu'
  | 'keyboard'
  | 'touch'

export type ReaderActionId =
  | 'back'
  | 'previous'
  | 'next'
  | 'reply'
  | 'reply_all'
  | 'forward'
  | 'done'
  | 'more'
  | 'find'
  | 'expand'
  | 'ping'
  | 'link_job'
  | 'send_to_inbox'
  | 'schedule'
  | 'mark_read'
  | 'star'
  | 'snooze'
  | 'archive'
  | 'junk'
  | 'delete'
  | 'who_can_see'
  | 'zoom_out'
  | 'zoom_reset'
  | 'zoom_in'
  | 'copy_sender_address'
  | 'copy_email_link'
  | 'accept_decision'
  | 'reject_decision'
  | 'open_card'
  | 'open_board'
  | 'manage_placements'
  | 'manage_company'
  | 'manage_classification'
  | 'manage_signals'
  | 'manage_owner'
  | 'manage_due'
  | 'manage_watchers'

/** Classified failure vocabulary shared by every reader action (plan: "Optimism, undo, failure"). */
export type ReaderErrorClass =
  | 'perm'
  | 'validation'
  | 'notfound'
  | 'conflict'
  | 'network'
  | 'server'

/** Operator-facing copy per failure class. Every class must be mapped so no toast falls back to raw text. */
export type ReaderErrorMapping = Readonly<Record<ReaderErrorClass, string>>

/** Coarse permission gate an action declares. Enforcement happens at the mutation seam, not here. */
export type ReaderActionPermission =
  | 'none'
  | 'email.read'
  | 'email.write'
  | 'email.delete'
  | 'kanban.write'
  | 'workflow.write'
  | 'crm.write'
  | 'org.move'

/** Acceptance story reference from the approved plan (US-01 through US-34). */
export type ReaderStoryId = `US-${string}`

/**
 * Named pure-reducer identity for optimistic paint. Phase 2 binds these ids to
 * real reducers; Phase 0 pins the vocabulary so every action declares intent.
 */
export type ReaderOptimisticReducerId =
  | 'none'
  | 'patch-email-flags'
  | 'remove-row-and-advance'
  | 'resolve-decision'
  | 'patch-workflow-cell'
  | 'patch-placements'
  | 'local-ui-only'

/** How an applied action is reversed. Terminal actions use the 8s undo toast (plan contract). */
export interface ReaderActionInverse {
  kind: 'none' | 'undo-toast' | 'toggle' | 'inverse-action'
  /** Only meaningful for `undo-toast`; the plan pins 8000ms. */
  undoWindowMs?: number
  description: string
}

/** At-rest owner slot class. `always`/`conditional` occupy a visible slot; `overflow` lives in More. */
export type ReaderActionVisibility = 'always' | 'conditional' | 'overflow'

/**
 * More-menu section an action renders under (plan "More menu and long-tail
 * actions"): Navigate (compact-only spillover), Communication, Workflow,
 * Access, State, View (full-view zoom), and the visually separated
 * destructive Risk group.
 */
export type ReaderOverflowGroup =
  | 'navigate'
  | 'communication'
  | 'workflow'
  | 'access'
  | 'state'
  | 'view'
  | 'risk'

/** Pure visibility predicate over the shared view model. Never fetches. */
export type ReaderVisibilityPredicate = (
  model: ReaderHeaderViewModel,
) => boolean

export interface ReaderActionDescriptor {
  id: ReaderActionId
  label: string
  zone: ReaderHeaderZone
  stableOrder: number
  inputs: readonly ReaderInputMethod[]
  visibility: ReaderActionVisibility
  /** Pure predicate refining `visibility` against live view-model state. */
  visibleWhen: ReaderVisibilityPredicate
  /** More-menu section; required for `overflow` visibility, optional spillover hint otherwise. */
  overflowGroup?: ReaderOverflowGroup
  /** One-sentence behavior contract for the primary (left-click/tap) invocation. */
  primaryAction: string
  /** Stable context-menu item ids owned by this action's right-click surface. */
  contextActions: readonly string[]
  permission: ReaderActionPermission
  optimistic: boolean
  optimisticReducer: ReaderOptimisticReducerId
  undoable: boolean
  inverse: ReaderActionInverse
  /** Independent busy scope so one in-flight action never blocks siblings. */
  busyKey: string
  errors: ReaderErrorMapping
  dangerous: boolean
  telemetryEvent: `reader_header.${string}`
  /** Acceptance stories (US-xx) that exercise this action. At least one required. */
  stories: readonly ReaderStoryId[]
}

export type ReaderDecisionKind =
  | 'organisation'
  | 'bill'
  | 'estimator'
  | 'placement'
  | 'suggested_org'
  | 'suggested_job'
  | 'classification'

/**
 * Deterministic decision priority (plan "Decision priority"):
 * cross-org/permission blocker > bill/financial (estimator allocation is the
 * quote-request financial-tier peer) > placement confirmation > suggested
 * job/org > classification/rule suggestion.
 */
export const READER_DECISION_PRIORITY: Readonly<
  Record<ReaderDecisionKind, number>
> = {
  organisation: 100,
  bill: 90,
  estimator: 85,
  placement: 80,
  suggested_org: 70,
  suggested_job: 65,
  classification: 50,
}

export interface ReaderDecision {
  id: string
  kind: ReaderDecisionKind
  priority: number
  label: string
  target: string
  /** Why the system proposes this (shown in the decision line / Review Queue). */
  explanation: string
  /** What changes when the operator confirms. */
  acceptEffect: string
  /** What changes when the operator rejects. */
  rejectEffect: string
  /** Who gains or loses sight of this email (Review Queue row, plan D269-12). */
  visibilityImpact: string
  reversible: boolean
  permission: ReaderActionPermission
  /**
   * Stale-version token. A mutation echoes this token; a mismatch means the
   * underlying state changed since the suggestion was built ("Changed since
   * suggested") and blind accept must be refused.
   */
  versionToken: string | null
  placement?: EmailAllocationSummary
}

export interface ReaderContextSummary {
  company: string | null
  classification: string | null
  signalCount: number
  placementCount: number
  watcherCount: number
  tokens: string[]
}

/** Notice families in strict severity order (plan "Notice Center before the body"). */
export type ReaderNoticeKind =
  | 'error'
  | 'provider'
  | 'ping'
  | 'calendar'
  | 'attachments'
  | 'info'

/** Lifecycle state a notice moves through. `blocking` renders inline, never in the collapsed center. */
export type ReaderNoticeLifecycle =
  | 'blocking'
  | 'active'
  | 'pending'
  | 'resolved'
  | 'acknowledged'

/** When a notice leaves the at-rest line. */
export type ReaderNoticeCollapsePolicy =
  | 'never-auto'
  | 'on-acknowledge'
  | 'on-resolve'
  | 'auto-after-acknowledge'

export interface ReaderNotice {
  id: string
  kind: ReaderNoticeKind
  /** Numeric severity from the notice registry; higher wins the at-rest slot. */
  severity: number
  lifecycle: ReaderNoticeLifecycle
  collapsePolicy: ReaderNoticeCollapsePolicy
  label: string
  /** Stable action ids from the notice registry's action set for this kind. */
  actions: readonly string[]
  actionLabel?: string
}

export interface ReaderHeaderViewModel {
  surface: ReaderHeaderSurface
  emailId: string
  subject: string
  senderName: string
  senderAddress: string
  context: ReaderContextSummary
  placements: EmailAllocationSummary[]
  primaryPlacement: EmailAllocationSummary | null
  decisions: ReaderDecision[]
  currentDecision: ReaderDecision | null
  notices: ReaderNotice[]
}
